import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Skeleton,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronRight,
  MessageSquare,
  Paperclip,
  RefreshCcw,
  Search,
  Workflow as WorkflowIcon,
  X,
} from 'lucide-react';

import {
  getWorkflowRuns,
  WorkflowRunListItem,
  WorkflowRunResult,
} from '$api/WorkflowRunsAPI';
import { getWorkflow, getWorkflowsCollection } from '$api/WorkflowsAPI';
import { TableHeadCustom, TablePaginationCustom } from '$components/table';
import { AdminShell } from '$layouts/v2/AdminShell';
import { Stack } from '$layouts/v2/components/Stack';
import { formatRelativeTime } from '$pages/mail-automation/utils';
import { useAccessToken } from '$src/authConfig';
import { useDebouncedValue } from '$src/hooks/use-debounced-value';

import {
  WORKFLOW_RUN_RESULT_META,
  WorkflowRunStatusBadge,
} from './WorkflowRunStatusBadge';

const TABLE_HEAD = [
  { id: 'result', label: 'Result', disableSort: true, width: 120 },
  { id: 'email', label: 'Email', disableSort: true, minWidth: 300 },
  { id: 'workflow', label: 'Workflow', disableSort: true, minWidth: 190 },
  { id: 'attachments', label: 'Attachments', disableSort: true, width: 145 },
  { id: 'activity', label: 'Activity', disableSort: true, width: 150 },
  { id: 'open', label: '', disableSort: true, width: 32 },
];

const RESULT_OPTIONS: { value: WorkflowRunResult | ''; label: string }[] = [
  { value: '', label: 'All results' },
  { value: 'failed', label: WORKFLOW_RUN_RESULT_META.failed.label },
  { value: 'success', label: WORKFLOW_RUN_RESULT_META.success.label },
  {
    value: 'inProcess',
    label: WORKFLOW_RUN_RESULT_META.inProcess.label,
  },
];

type Filters = {
  search: string;
  result: WorkflowRunResult | '';
  workflowId: string;
  workflowVersion: number | '';
  startDate: string;
  endDate: string;
};

type WorkflowVersionFilterOption = {
  version: number;
  statusLabel: string;
  statusColor: 'default' | 'success' | 'warning';
};

type WorkflowFilterOption = {
  workflowId: string;
  title: string;
  activeVersion: number | null;
  statusLabel: string;
  statusColor: 'default' | 'success' | 'warning';
  versions: WorkflowVersionFilterOption[];
};

const DEFAULT_FILTERS: Filters = {
  search: '',
  result: '',
  workflowId: '',
  workflowVersion: '',
  startDate: '',
  endDate: '',
};

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const formatCompactDateTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const toDate = (value: string) => (value ? new Date(value) : null);

export default function WorkflowRunsPage() {
  const { token } = useAccessToken();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialWorkflowId = searchParams.get('workflowId') ?? '';
  const initialWorkflowVersionValue = Number(
    searchParams.get('workflowVersion'),
  );
  const initialWorkflowVersion =
    Number.isInteger(initialWorkflowVersionValue) &&
    initialWorkflowVersionValue > 0
      ? initialWorkflowVersionValue
      : '';
  const [items, setItems] = useState<WorkflowRunListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [workflows, setWorkflows] = useState<WorkflowFilterOption[]>([]);
  const [filters, setFilters] = useState<Filters>(() => ({
    ...DEFAULT_FILTERS,
    workflowId: initialWorkflowId,
    workflowVersion: initialWorkflowVersion,
  }));
  const debouncedFilters = useDebouncedValue(filters, 250);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestRequestIdRef = useRef(0);

  const load = useCallback(() => {
    if (!token) {
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    setIsLoading(true);
    setError(null);

    getWorkflowRuns(token, {
      filters: {
        search: debouncedFilters.search.trim() || undefined,
        result: debouncedFilters.result,
        workflowId: debouncedFilters.workflowId || undefined,
        workflowVersion: debouncedFilters.workflowVersion || undefined,
        startDate: toDate(debouncedFilters.startDate),
        endDate: toDate(debouncedFilters.endDate),
      },
      pagination,
    })
      .then((response) => {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        setItems(response.items);
        setTotalItems(response.totalItems);
        setIsLoading(false);
      })
      .catch((error_) => {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        setError(String(error_));
        setIsLoading(false);
      });
  }, [token, debouncedFilters, pagination]);

  useEffect(() => {
    if (!token) {
      return;
    }
    getWorkflowsCollection(token)
      .then((response) =>
        Promise.all(
          response
            .filter((workflow) => workflow.trigger === 'email')
            .map(async (workflow) => {
              try {
                const detail = await getWorkflow(token, workflow.workflowId);
                const workflowIsLive = detail.head.activeVersion !== null;
                const workflowHasDraft = detail.versions.some(
                  (version) => version.status === 'draft',
                );
                const statusLabel = workflowIsLive
                  ? `Live v${detail.head.activeVersion}`
                  : workflowHasDraft
                  ? 'Draft'
                  : 'Not live';
                const statusColor: WorkflowFilterOption['statusColor'] =
                  workflowIsLive
                    ? 'success'
                    : workflowHasDraft
                    ? 'warning'
                    : 'default';
                return {
                  workflowId: workflow.workflowId,
                  title: workflow.title,
                  activeVersion: detail.head.activeVersion,
                  statusLabel,
                  statusColor,
                  versions: detail.versions
                    .map((version): WorkflowVersionFilterOption => {
                      const versionIsLive =
                        version.version === detail.head.activeVersion;
                      return {
                        version: version.version,
                        statusLabel: versionIsLive
                          ? 'Live'
                          : version.status === 'draft'
                          ? 'Draft'
                          : 'Archived',
                        statusColor: versionIsLive
                          ? 'success'
                          : version.status === 'draft'
                          ? 'warning'
                          : 'default',
                      };
                    })
                    .toSorted((a, b) => b.version - a.version),
                };
              } catch {
                return {
                  workflowId: workflow.workflowId,
                  title: workflow.title,
                  activeVersion: null,
                  statusLabel: 'Status unavailable',
                  statusColor: 'default' as const,
                  versions: [],
                };
              }
            }),
        ),
      )
      .then(setWorkflows)
      .catch(() => setWorkflows([]));
  }, [token]);

  useEffect(() => load(), [load]);

  const resetPage = () =>
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));

  const setFilter = <Key extends keyof Filters>(
    key: Key,
    value: Filters[Key],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    resetPage();
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);
  const workflowTitles = useMemo(
    () =>
      new Map(
        workflows.map((workflow) => [workflow.workflowId, workflow.title]),
      ),
    [workflows],
  );
  const isInitialLoading = isLoading && items.length === 0;

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    resetPage();
  };

  const workflowFilterValue = JSON.stringify({
    workflowId: filters.workflowId,
    workflowVersion: filters.workflowVersion,
  });
  const selectedWorkflow = workflows.find(
    (candidate) => candidate.workflowId === filters.workflowId,
  );
  const selectedWorkflowIsKnown =
    !filters.workflowId ||
    Boolean(
      selectedWorkflow &&
        (filters.workflowVersion === '' ||
          selectedWorkflow.versions.some(
            (version) => version.version === filters.workflowVersion,
          )),
    );

  const selectWorkflowFilter = (value: string) => {
    if (!value) {
      setFilters((previous) => ({
        ...previous,
        workflowId: '',
        workflowVersion: '',
      }));
      resetPage();
      return;
    }

    try {
      const selection = JSON.parse(value) as {
        workflowId?: unknown;
        workflowVersion?: unknown;
      };
      if (typeof selection.workflowId !== 'string') return;
      const workflowId = selection.workflowId;

      setFilters((previous) => ({
        ...previous,
        workflowId,
        workflowVersion:
          typeof selection.workflowVersion === 'number'
            ? selection.workflowVersion
            : '',
      }));
      resetPage();
    } catch {
      // Ignore an invalid selector value.
    }
  };

  const openRun = (id: string) => {
    navigate(`/v2/mail-automation/workflow-runs/${encodeURIComponent(id)}`);
  };

  return (
    <AdminShell
      title="Workflow Runs"
      titleIcon={<WorkflowIcon size={20} strokeWidth={2} />}
      actions={
        <Button startIcon={<RefreshCcw size={14} />} onClick={load}>
          Refresh
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{
            p: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
          }}
        >
          <TextField
            fullWidth={false}
            placeholder="Search subject or email ID…"
            value={filters.search}
            onChange={(event) => setFilter('search', event.target.value)}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 240 },
              flex: { sm: '1 1 280px' },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={14} />
                  </InputAdornment>
                ),
              },

              htmlInput: { 'aria-label': 'Search by email subject or ID' },
            }}
          />

          <TextField
            select
            fullWidth={false}
            value={filters.result}
            onChange={(event) =>
              setFilter('result', event.target.value as WorkflowRunResult | '')
            }
            sx={{ width: { xs: '100%', sm: 128 } }}
            label={'Status'}
            slotProps={{
              htmlInput: { 'aria-label': 'Filter by result' },
            }}
          >
            {RESULT_OPTIONS.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <FormControl size="small" sx={{ width: { xs: '100%', sm: 245 } }}>
            <InputLabel id="workflow-version-filter-label">
              Workflow and version
            </InputLabel>
            <Select
              labelId="workflow-version-filter-label"
              label="Workflow and version"
              value={filters.workflowId ? workflowFilterValue : ''}
              onChange={(event) => selectWorkflowFilter(event.target.value)}
              renderValue={() => {
                if (!filters.workflowId) return 'All workflows and versions';
                const workflowLabel =
                  selectedWorkflow?.title ?? filters.workflowId;
                const versionStatus =
                  filters.workflowVersion === ''
                    ? selectedWorkflow?.statusLabel
                    : selectedWorkflow?.versions.find(
                        (version) =>
                          version.version === filters.workflowVersion,
                      )?.statusLabel;
                return filters.workflowVersion === ''
                  ? `${workflowLabel} · All versions${
                      versionStatus ? ` · ${versionStatus}` : ''
                    }`
                  : `${workflowLabel} · Version ${filters.workflowVersion}${
                      versionStatus ? ` · ${versionStatus}` : ''
                    }`;
              }}
              inputProps={{ 'aria-label': 'Filter by workflow and version' }}
            >
              <MenuItem value="">All workflows and versions</MenuItem>
              {filters.workflowId && !selectedWorkflowIsKnown && (
                <MenuItem value={workflowFilterValue} disabled>
                  {selectedWorkflow?.title ?? filters.workflowId} · Version{' '}
                  {filters.workflowVersion}
                </MenuItem>
              )}
              {workflows.flatMap((workflow) => [
                <ListSubheader
                  key={`${workflow.workflowId}:header`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <span>{workflow.title}</span>
                  <Chip
                    size="small"
                    color={workflow.statusColor}
                    variant="outlined"
                    label={workflow.statusLabel}
                  />
                </ListSubheader>,
                <MenuItem
                  key={`${workflow.workflowId}:all`}
                  value={JSON.stringify({
                    workflowId: workflow.workflowId,
                    workflowVersion: '',
                  })}
                  sx={{ pl: 3 }}
                >
                  <Box sx={{ flex: 1 }}>All versions</Box>
                  <Chip
                    size="small"
                    color={workflow.statusColor}
                    variant="outlined"
                    label={workflow.statusLabel}
                  />
                </MenuItem>,
                ...workflow.versions.map((version) => (
                  <MenuItem
                    key={`${workflow.workflowId}:v${version.version}`}
                    value={JSON.stringify({
                      workflowId: workflow.workflowId,
                      workflowVersion: version.version,
                    })}
                    sx={{ pl: 3 }}
                  >
                    <Box sx={{ flex: 1 }}>Version {version.version}</Box>
                    <Chip
                      size="small"
                      color={version.statusColor}
                      variant="outlined"
                      label={version.statusLabel}
                    />
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>

          <TextField
            fullWidth={false}
            label="From"
            type="date"
            value={filters.startDate}
            onChange={(event) => setFilter('startDate', event.target.value)}
            sx={{ width: { xs: '100%', sm: 138 } }}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />

          <TextField
            fullWidth={false}
            label="To"
            type="date"
            value={filters.endDate}
            onChange={(event) => setFilter('endDate', event.target.value)}
            sx={{ width: { xs: '100%', sm: 138 } }}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />

          {hasActiveFilters && (
            <Tooltip title="Clear filters">
              <IconButton size="small" onClick={clearFilters}>
                <X size={16} />
              </IconButton>
            </Tooltip>
          )}

          <Chip
            size="small"
            variant="outlined"
            label={`${totalItems.toLocaleString()} ${
              totalItems === 1 ? 'run' : 'runs'
            }`}
            sx={{
              ml: { sm: 'auto' },
              opacity: isLoading ? 0.65 : 1,
              alignSelf: { xs: 'flex-start', sm: 'center' },
            }}
          />
        </Stack>

        <Box sx={{ height: 2 }}>
          {isLoading && <LinearProgress sx={{ height: 2 }} />}
        </Box>

        <TableContainer
          sx={{
            maxHeight: { xs: 'none', md: 'calc(100vh - 205px)' },
            minHeight: 240,
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 925,
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                px: 1.25,
                py: 0.65,
                borderColor: 'divider',
              },
              '& .MuiTableCell-head': {
                py: 0.75,
                bgcolor: 'background.paper',
                color: 'text.secondary',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.35,
                textTransform: 'uppercase',
              },
            }}
          >
            <TableHeadCustom
              headLabel={TABLE_HEAD}
              sx={{ whiteSpace: 'nowrap' }}
            />
            <TableBody>
              {isInitialLoading
                ? Array.from({
                    length: Math.min(pagination.rowsPerPage, 8),
                  }).map((_, index) => (
                    <TableRow key={index} sx={{ height: 44 }}>
                      {TABLE_HEAD.map((column) => (
                        <TableCell key={column.id}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : items.map((row) => {
                    const workflowTitle = workflowTitles.get(row.workflowId);

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          height: 46,
                          cursor: 'pointer',
                          '&:hover .workflow-run-chevron': {
                            color: 'text.primary',
                            transform: 'translateX(2px)',
                          },
                        }}
                        onClick={() => openRun(row.id)}
                      >
                        <TableCell>
                          <WorkflowRunStatusBadge result={row.result} />
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{ mt: 0.25, color: 'text.secondary' }}
                          >
                            <MessageSquare size={11} />
                            <Typography
                              variant="caption"
                              sx={{ lineHeight: 1.2 }}
                            >
                              {row.messageCount}{' '}
                              {row.messageCount === 1 ? 'message' : 'messages'}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ minWidth: 0 }}>
                          <Tooltip title={row.subject || row.emailId}>
                            <Typography
                              component={RouterLink}
                              to={`/v2/mail-automation/workflow-runs/${encodeURIComponent(
                                row.id,
                              )}`}
                              aria-label={`Open workflow run ${
                                row.subject || row.emailId
                              }`}
                              onClick={(event) => event.stopPropagation()}
                              noWrap
                              sx={{
                                display: 'block',
                                color: 'text.primary',
                                textDecoration: 'none',
                                fontSize: 13,
                                fontWeight: 600,
                                lineHeight: 1.35,
                                '&:focus-visible': {
                                  outline: '2px solid',
                                  outlineColor: 'secondary.main',
                                  outlineOffset: 1,
                                  borderRadius: 0.5,
                                },
                              }}
                            >
                              {row.subject || row.emailId}
                            </Typography>
                          </Tooltip>
                          <Tooltip
                            title={`${row.mailbox?.mailAddress ?? '-'} · ${
                              row.emailId
                            }`}
                          >
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ display: 'block', lineHeight: 1.35 }}
                            >
                              {row.mailbox?.mailAddress ?? '-'} · {row.emailId}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell sx={{ minWidth: 0 }}>
                          <Tooltip title={workflowTitle ?? row.workflowId}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontWeight: 500, lineHeight: 1.35 }}
                            >
                              {workflowTitle ?? row.workflowId}
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ display: 'block', lineHeight: 1.35 }}
                          >
                            {workflowTitle ? `${row.workflowId} · ` : ''}v
                            {row.workflowVersion}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.35}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Paperclip size={12} />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, lineHeight: 1.35 }}
                              >
                                {row.attachments.total}{' '}
                                {row.attachments.total === 1
                                  ? 'attachment'
                                  : 'attachments'}
                              </Typography>
                            </Stack>
                            {row.attachments.total > 0 && (
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                {row.attachments.processingStatus === 'ready'
                                  ? `${row.attachments.processed} processed`
                                  : row.attachments.processingStatus ===
                                    'pending'
                                  ? `${
                                      row.attachments.pending +
                                      row.attachments.processing
                                    } processing`
                                  : 'Not required'}
                                {row.attachments.failed
                                  ? ` · ${row.attachments.failed} failed`
                                  : ''}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Tooltip
                            title={`Updated ${formatDateTime(row.updatedAt)}`}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontWeight: 500, lineHeight: 1.35 }}
                            >
                              {formatRelativeTime(row.updatedAt)}
                            </Typography>
                          </Tooltip>
                          <Tooltip
                            title={`Started ${formatDateTime(row.createdAt)}`}
                          >
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ display: 'block', lineHeight: 1.35 }}
                            >
                              Started {formatCompactDateTime(row.createdAt)}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ color: 'text.secondary' }}
                        >
                          <ChevronRight
                            className="workflow-run-chevron"
                            size={15}
                            style={{
                              display: 'block',
                              transition:
                                'color 120ms ease, transform 120ms ease',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}

              {!isInitialLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length}>
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                      <Typography
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        No workflow runs found.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePaginationCustom
          count={totalItems}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_event, page) =>
            setPagination((prev) => ({ ...prev, page }))
          }
          onRowsPerPageChange={(event) =>
            setPagination({
              page: 0,
              rowsPerPage: Number.parseInt(event.target.value, 10),
            })
          }
          sx={{
            '& .MuiTablePagination-toolbar': { minHeight: 44 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              { my: 0 },
          }}
        />
      </Card>
    </AdminShell>
  );
}
